import { Routes } from "@angular/router";
import { AuthGuard } from "../Authentication/guards/auth.guard";
import { RoleGuard } from "../Authentication/guards/role.guard";
import { Role } from "../Authentication/models/roles-permissions.model";
import { NgxPermissionsRouterData } from "ngx-permissions";

export const CUSTOMER_ROUTES: Routes = [
    {
        path: "",
        canActivate: [RoleGuard],
        data: {
            roles: {
                only: [
                    Role.Customer,
                    Role.Guest,
                    Role.SiteAdministrator,
                    Role.Vendor,
                    Role.BusinessCustomer,
                ],
            } as NgxPermissionsRouterData,
        },
        children: [
            {
                path: "dashboard",
                loadComponent: () =>
                    import("./components/home-dashboard/home-dashboard.component").then(
                        (m) => m.HomeDashboard
                    ),
            },
            {
                path: "landing",
                loadComponent: () =>
                    import(
                        "./components/Business-related/business-landing-page/business-landing-page.component"
                    ).then((m) => m.BusinessLandingPageComponent),
            },
            {
                path: "pricing-plans",
                loadComponent: () =>
                    import("./components/pricing-plans/pricing-plans.component").then(
                        (m) => m.PricingPlansComponent
                    ),
            },
            {
                path: "main-dashboard",
                loadComponent: () =>
                    import("./components/main-dashboard/main-dashboard.component").then(
                        (m) => m.MainDashboardComponent
                    ),
            },
            {
                path: "details/:catalogItemStyle",
                loadComponent: () =>
                    import(
                        "./components/product-related/product-details/product-details.component"
                    ).then((m) => m.ProductDetailsComponent),
            },
            {
                path: ":catalogItem/:catalogItemStyle/print-product-details",
                loadComponent: () =>
                    import(
                        "./components/product-related/product-details/print-product-details/print-product-details.component"
                    ).then((m) => m.PrintProductDetailsComponent),
            },
            {
                path: ":catalogItemStyle/product-click-zoom",
                loadComponent: () =>
                    import(
                        "./components/product-related/product-details/product-click-zoom/product-click-zoom.component"
                    ).then((m) => m.ProductClickZoomComponent),
            },
            {
                path: "shop-by-category",
                loadComponent: () =>
                    import(
                        "./components/category-dashboard/category-dashboard.component"
                    ).then((m) => m.CategoryDashboardComponent),
            },
            {
                path: "register-your-business",
                loadComponent: () =>
                    import(
                        "./components/Business-related/business-registeration/business-registration.component"
                    ).then((m) => m.BusinessRegistrationComponent),
            },
        ],
    },
    {
        path: "order",
        canActivate: [RoleGuard],
        data: {
            roles: {
                only: [Role.Customer, Role.BusinessCustomer],
                redirectTo: "/error/access-denied",
            } as NgxPermissionsRouterData,
        },
        children: [
            {
                path: "customer-plans",
                loadComponent: () =>
                    import("./components/customer-plans/customer-plans.component").then(
                        (m) => m.CustomerPlansComponent
                    ),
            },
            {
                path: "myOrders",
                loadComponent: () =>
                    import(
                        "./components/order-related/my-orders/my-orders.component"
                    ).then((m) => m.MyOrdersComponent),
            },
            {
                path: "confirmation",
                loadComponent: () =>
                    import(
                        "./components/order-related/order-confirmation/order-confirmation.component"
                    ).then((m) => m.OrderConfirmationComponent),
            },
            {
                path: "details/:webOrder",
                loadComponent: () =>
                    import(
                        "./components/order-related/order-details/order-details.component"
                    ).then((m) => m.OrderDetailsComponent),
            },
            {
                path: "review-your-purchase/:catalogItem",
                loadComponent: () =>
                    import(
                        "./components/order-related/review-purchases/review-purchases.component"
                    ).then((m) => m.ReviewPurchasesComponent),
            },
            {
                path: "returnOrder/:webOrderDetail",
                loadComponent: () =>
                    import(
                        "./components/order-related/return-order/return-order.component"
                    ).then((m) => m.ReturnOrderComponent),
            },
            {
                path: "cancelOrder/:webOrder",
                loadComponent: () =>
                    import(
                        "./components/order-related/cancel-items/cancel-items.component"
                    ).then((m) => m.CancelItemsComponent),
            },
            {
                path: "returnInstructions/:webOrderDetail",
                loadComponent: () =>
                    import(
                        "./components/order-related/return-instructions/return-instructions.component"
                    ).then((m) => m.ReturnInstructionsComponent),
            },
            {
                path: "transactionSummary/:webOrderDetail",
                loadComponent: () =>
                    import(
                        "./components/order-related/transaction-summary/transaction-summary.component"
                    ).then((m) => m.TransactionSummaryComponent),
            },
            {
                path: "trackOrder/:webOrderDetail",
                loadComponent: () =>
                    import(
                        "./components/order-related/track-order/track-order.component"
                    ).then((m) => m.TrackOrderComponent),
            },
            {
                path: "invoice",
                loadComponent: () =>
                    import(
                        "./components/order-related/download-invoice/download-invoice.component"
                    ).then((m) => m.DownloadInvoiceComponent),
            },
            {
                path: "view-customer-query",
                loadComponent: () =>
                    import(
                        "../Shared/components/view-customer-query/view-customer-query.component"
                    ).then((m) => m.ViewCustomerQueryComponent),
            },
        ],
    },
    {
        path: "",
        canActivate: [RoleGuard],
        data: {
            roles: {
                only: [Role.Customer, Role.BusinessCustomer],
                redirectTo: "/error/access-denied",
            } as NgxPermissionsRouterData,
        },
        children: [
            {
                path: "verifyBusCustomer",
                loadComponent: () =>
                    import("../vendor/components/verify-vendor/verify-vendor.component").then(
                        (m) => m.VerifyVendorComponent
                    ),
            },
            {
                path: "wishlist",
                canActivate: [AuthGuard], // Additional AuthGuard check on this specific route
                loadComponent: () =>
                    import(
                        "./components/wishlist-related/wishlist/wishlist.component"
                    ).then((m) => m.WishlistComponent),
            },
            {
                path: "customer-service",
                loadComponent: () =>
                    import(
                        "./components/customer-service/customer-service.component"
                    ).then((m) => m.CustomerServiceComponent),
            },
            {
                path: "updateProfile",
                loadComponent: () =>
                    import(
                        "../Shared/components/update-profile/update-profile.component"
                    ).then((m) => m.UpdateProfileComponent),
            },
            {
                path: "your-address",
                loadComponent: () =>
                    import("./components/your-addresses/your-addresses.component").then(
                        (m) => m.YourAddressesComponent
                    ),
            },
            {
                path: "account",
                loadComponent: () =>
                    import(
                        "./components/accounts-update-page/accounts-update-page.component"
                    ).then((m) => m.AccountsUpdatePageComponent),
            },
        ],
    },

    {
        path: "",
        canActivate: [RoleGuard],
        data: {
            roles: {
                only: [Role.Customer, Role.Guest, Role.BusinessCustomer],
                redirectTo: "/error/access-denied",
            } as NgxPermissionsRouterData,
        },
        children: [
            {
                path: "viewCart",
                loadComponent: () =>
                    import("./components/cart-related/view-cart/view-cart.component").then(
                        (m) => m.ViewCartComponent
                    ),
            },
            {
                path: "cart-checkout",
                loadComponent: () =>
                    import(
                        "./components/cart-related/cart-steps/cart-steps.component"
                    ).then((m) => m.CartStepsComponent),
            },
        ],
    },

    // --- Group 5: Return Status Path (Broader Roles) ---
    {
        path: "order",
        canActivate: [RoleGuard],
        data: {
            roles: {
                only: [
                    Role.Customer,
                    Role.Vendor,
                    Role.WholeSaler,
                    Role.BusinessCustomer,
                ],
                redirectTo: "/error/access-denied",
            } as NgxPermissionsRouterData,
        },
        children: [
            {
                path: "returnStatus/:webOrderDetail",
                loadComponent: () =>
                    import(
                        "./components/order-related/return-status/return-status.component"
                    ).then((m) => m.ReturnStatusComponent),
            },
        ],
    },
];  